import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_VERSION } from '../config/version';
import { useToast } from '../hooks';
import { useAuthStore, useChatStore, useDriverShiftStore, useMarketplaceStore, useTrackingStore } from '../stores';
import {
    dispatchPushByType,
    ensurePushPermissions,
    getCachedPushToken,
    getPushPlatform,
    initializePushListeners,
    isPushNativeSupported,
    registerPushClient,
    teardownPushListeners,
} from '../services/push/pushService';
import { registerPushDevice } from '../services/push/pushApi';
import { subscribeToUserChatMessages } from '../services/chatRealtime';
import type { PushApiError, PushHandlerMap, PushNotificationPayload } from '../types/push';
import { resolveDeepLink } from '../utils/deepLinkResolver';
import type { TrackingIntentAction, TrackingPageNavigationState } from '../pages/marketplace/trackingNavigation';

interface PushProviderProps {
    children: ReactNode;
}

function parseConversationIdFromNotification(notification: PushNotificationPayload): number | null {
    const metaConversationId = notification.meta?.conversation_id;

    if (typeof metaConversationId === 'number' && Number.isInteger(metaConversationId) && metaConversationId > 0) {
        return metaConversationId;
    }

    if (typeof metaConversationId === 'string') {
        const parsed = Number(metaConversationId);
        if (Number.isInteger(parsed) && parsed > 0) {
            return parsed;
        }
    }

    const resolvedDeepLink = resolveDeepLink(notification.deep_link);
    const match = resolvedDeepLink.match(/^\/chat\/conversations\/(\d+)(?:[/?#].*)?$/);
    if (!match) {
        return null;
    }

    const conversationId = Number(match[1]);
    return Number.isInteger(conversationId) && conversationId > 0 ? conversationId : null;
}

function parseContractIdFromNotification(notification: PushNotificationPayload): number | null {
    const metaContractId = notification.meta?.contract_id;

    if (typeof metaContractId === 'number' && Number.isInteger(metaContractId) && metaContractId > 0) {
        return metaContractId;
    }

    if (typeof metaContractId === 'string') {
        const parsed = Number(metaContractId);
        if (Number.isInteger(parsed) && parsed > 0) {
            return parsed;
        }
    }

    const rawContractId = notification.rawData.contract_id;

    if (typeof rawContractId === 'number' && Number.isInteger(rawContractId) && rawContractId > 0) {
        return rawContractId;
    }

    if (typeof rawContractId === 'string') {
        const parsed = Number(rawContractId);
        if (Number.isInteger(parsed) && parsed > 0) {
            return parsed;
        }
    }

    const resolvedDeepLink = resolveDeepLink(notification.deep_link);
    const match = resolvedDeepLink.match(/^\/marketplace\/contracts\/(\d+)(?:[/?#].*)?$/);
    if (!match) {
        return null;
    }

    const contractId = Number(match[1]);
    return Number.isInteger(contractId) && contractId > 0 ? contractId : null;
}

function parseTrackingEventType(notification: PushNotificationPayload): string | null {
    const rawEventType = notification.rawData.event_type;
    if (typeof rawEventType === 'string' && rawEventType.trim() !== '') {
        return rawEventType;
    }

    const metaEventType = notification.meta?.event_type;
    return typeof metaEventType === 'string' && metaEventType.trim() !== '' ? metaEventType : null;
}

function parseTrackingAction(notification: PushNotificationPayload): string | null {
    const rawAction = notification.rawData.action;
    if (typeof rawAction === 'string' && rawAction.trim() !== '') {
        return rawAction;
    }

    const metaAction = notification.meta?.action;
    return typeof metaAction === 'string' && metaAction.trim() !== '' ? metaAction : null;
}

function resolveTrackingIntentAction(notification: PushNotificationPayload): TrackingIntentAction | null {
    const action = parseTrackingAction(notification);
    const eventType = parseTrackingEventType(notification);

    if (action === 'confirm_origin_arrival') {
        return 'mark_origin_arrived';
    }

    if (action === 'confirm_destination_arrival') {
        return 'mark_destination_arrived';
    }

    if (!action) {
        if (eventType === 'operator_arrived_origin') {
            return 'mark_origin_arrived';
        }

        if (eventType === 'operator_arrived_destination') {
            return 'mark_destination_arrived';
        }
    }

    return null;
}

function buildTrackingDeepLink(notification: PushNotificationPayload): string {
    const resolved = resolveDeepLink(notification.deep_link);
    if (resolved !== '/') {
        return resolved;
    }

    const contractId = parseContractIdFromNotification(notification);
    return contractId ? `/marketplace/contracts/${contractId}/tracking` : '/marketplace/contracts';
}

function parseRequestIdFromNotification(notification: PushNotificationPayload): number | null {
    const metaRequestId = notification.meta?.shipment_request_id;

    if (typeof metaRequestId === 'number' && Number.isInteger(metaRequestId) && metaRequestId > 0) {
        return metaRequestId;
    }

    if (typeof metaRequestId === 'string') {
        const parsed = Number(metaRequestId);
        if (Number.isInteger(parsed) && parsed > 0) {
            return parsed;
        }
    }

    const resolvedDeepLink = resolveDeepLink(notification.deep_link);
    const match = resolvedDeepLink.match(/^\/marketplace\/requests\/(\d+)(?:[/?#].*)?$/);
    if (!match) {
        return null;
    }

    const requestId = Number(match[1]);
    return Number.isInteger(requestId) && requestId > 0 ? requestId : null;
}

function isRouteMatch(currentRoute: string, targetRoute: string): boolean {
    return currentRoute === targetRoute
        || currentRoute.startsWith(`${targetRoute}?`)
        || currentRoute.startsWith(`${targetRoute}#`);
}

/**
 * Provider global para notificaciones push (solo móvil nativo).
 * Sprint 10 - HU-FE-01/02/03/05
 */
export function PushProvider({ children }: PushProviderProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const userId = useAuthStore((state) => state.user?.id ?? null);
    const userOperationMode = useAuthStore((state) => state.user?.operation_mode ?? null);
    const authToken = useAuthStore((state) => state.token);
    const authInitialized = useAuthStore((state) => state.isInitialized);
    const fetchConversations = useChatStore((state) => state.fetchConversations);
    const chatConversations = useChatStore((state) => state.conversations);
    const conversationsMeta = useChatStore((state) => state.conversationsMeta);
    const isLoadingConversations = useChatStore((state) => state.isLoadingConversations);
    const fetchConversation = useChatStore((state) => state.fetchConversation);
    const fetchMessages = useChatStore((state) => state.fetchMessages);
    const incrementUnreadFromPush = useChatStore((state) => state.incrementUnreadFromPush);
    const appendRealtimeMessage = useChatStore((state) => state.appendRealtimeMessage);
    const activeConversationId = useChatStore((state) => state.activeConversationId);
    const fetchFeedRequests = useMarketplaceStore((state) => state.fetchFeedRequests);
    const fetchRequestDetail = useMarketplaceStore((state) => state.fetchRequestDetail);
    const fetchMyContracts = useMarketplaceStore((state) => state.fetchMyContracts);
    const fetchContractDetail = useMarketplaceStore((state) => state.fetchContractDetail);
    const fetchTrackingEntry = useTrackingStore((state) => state.fetchAll);
    const loadCurrentShift = useDriverShiftStore((state) => state.loadCurrentShift);

    const isAuthenticated = authInitialized && userId !== null && Boolean(authToken);
    const currentRouteRef = useRef(`${location.pathname}${location.search}${location.hash}`);
    const lastSyncedTokenRef = useRef<string | null>(null);
    const chatRealtimeConnectedRef = useRef(false);

    useEffect(() => {
        currentRouteRef.current = `${location.pathname}${location.search}${location.hash}`;
    }, [location.pathname, location.search, location.hash]);

    useEffect(() => {
        if (!isAuthenticated || conversationsMeta !== null || isLoadingConversations) {
            return;
        }

        void fetchConversations();
    }, [isAuthenticated, conversationsMeta, isLoadingConversations, fetchConversations]);

    useEffect(() => {
        if (!authToken || !isAuthenticated || userId === null) {
            chatRealtimeConnectedRef.current = false;
            return;
        }

        try {
            const unsubscribe = subscribeToUserChatMessages(
                userId,
                authToken,
                appendRealtimeMessage,
                (connected) => {
                    const wasConnected = chatRealtimeConnectedRef.current;
                    chatRealtimeConnectedRef.current = connected;

                    if (connected && !wasConnected) {
                        void fetchConversations();
                    }
                },
            );

            return () => {
                chatRealtimeConnectedRef.current = false;
                unsubscribe();
            };
        } catch (error) {
            chatRealtimeConnectedRef.current = false;
            console.warn('[PushProvider] Error inicializando realtime global de chat:', {
                userId,
                error,
            });

            return undefined;
        }
    }, [authToken, isAuthenticated, userId, appendRealtimeMessage, fetchConversations]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        let refreshInFlight = false;

        const refreshWhenDisconnected = async () => {
            if (
                chatRealtimeConnectedRef.current
                || refreshInFlight
                || document.visibilityState !== 'visible'
            ) {
                return;
            }

            refreshInFlight = true;
            try {
                await fetchConversations();
            } finally {
                refreshInFlight = false;
            }
        };

        const intervalId = window.setInterval(() => {
            void refreshWhenDisconnected();
        }, 5000);
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void refreshWhenDisconnected();
            }
        };
        const handleOnline = () => {
            void refreshWhenDisconnected();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('online', handleOnline);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('online', handleOnline);
        };
    }, [isAuthenticated, fetchConversations]);

    const navigateToDeepLink = useCallback((rawDeepLink?: string, state?: TrackingPageNavigationState) => {
        const targetRoute = resolveDeepLink(rawDeepLink);
        if (currentRouteRef.current === targetRoute && !state) {
            return;
        }

        navigate(targetRoute, state ? { state } : undefined);
    }, [navigate]);

    const handleTrackingNavigation = useCallback((notification: PushNotificationPayload) => {
        const targetRoute = buildTrackingDeepLink(notification);
        const intentAction = resolveTrackingIntentAction(notification);
        const navigationState = intentAction
            ? { trackingIntentAction: intentAction, source: 'push' as const }
            : undefined;

        if (currentRouteRef.current === targetRoute && !navigationState) {
            return;
        }

        navigate(targetRoute, navigationState ? { state: navigationState } : undefined);
    }, [navigate]);

    const refreshChatFromPush = useCallback(async (notification: PushNotificationPayload) => {
        if (!isAuthenticated) {
            return;
        }

        const conversationId = parseConversationIdFromNotification(notification);

        // Mantener actualizado el listado de conversaciones.
        void fetchConversations();

        if (!conversationId) {
            return;
        }

        const isKnownConversation = chatConversations.some((conversation) => conversation.id === conversationId);

        // Fallback: si la conversación no está cargada en memoria local,
        // incrementamos el total para no esperar a abrir la pestaña de chat.
        if (!isKnownConversation) {
            incrementUnreadFromPush(conversationId);
        }

        // Mantener actualizado el header/estado de la conversación.
        void fetchConversation(conversationId);

        const currentRoute = currentRouteRef.current;
        const isOnSameConversation =
            currentRoute === `/chat/conversations/${conversationId}` ||
            currentRoute.startsWith(`/chat/conversations/${conversationId}?`) ||
            currentRoute.startsWith(`/chat/conversations/${conversationId}#`);

        if (isOnSameConversation) {
            // Fallback si realtime no está activo: refrescar mensajes visibles.
            void fetchMessages(conversationId, { page: 1, per_page: 30, append: false });
        }
    }, [isAuthenticated, chatConversations, fetchConversations, incrementUnreadFromPush, fetchConversation, fetchMessages]);

    const refreshContractsFromPush = useCallback(async (notification: PushNotificationPayload) => {
        if (!isAuthenticated) {
            return;
        }

        void fetchMyContracts();

        const contractId = parseContractIdFromNotification(notification);
        if (!contractId) {
            return;
        }

        const currentRoute = currentRouteRef.current;
        const contractRoute = `/marketplace/contracts/${contractId}`;
        const trackingRoute = `${contractRoute}/tracking`;
        const isOnSameContract = isRouteMatch(currentRoute, contractRoute);
        const isOnSameTracking = isRouteMatch(currentRoute, trackingRoute);

        if (isOnSameContract) {
            void fetchContractDetail(contractId);
        }

        if (isOnSameTracking) {
            void Promise.all([
                fetchContractDetail(contractId),
                fetchTrackingEntry(contractId),
            ]);
        }
    }, [isAuthenticated, fetchMyContracts, fetchContractDetail, fetchTrackingEntry]);

    const refreshRequestsFromPush = useCallback(async (notification: PushNotificationPayload) => {
        if (!isAuthenticated || userOperationMode !== 'operator') {
            return;
        }

        void fetchFeedRequests();

        const requestId = parseRequestIdFromNotification(notification);
        if (!requestId) {
            return;
        }

        const currentRoute = currentRouteRef.current;
        const isOnSameRequest = isRouteMatch(currentRoute, `/marketplace/requests/${requestId}`);

        if (isOnSameRequest) {
            void fetchRequestDetail(requestId);
        }
    }, [isAuthenticated, userOperationMode, fetchFeedRequests, fetchRequestDetail]);

    const refreshDriverShiftFromPush = useCallback(async () => {
        if (!isAuthenticated || userOperationMode !== 'operator') {
            return;
        }

        await loadCurrentShift();
    }, [isAuthenticated, userOperationMode, loadCurrentShift]);

    const actionHandlers = useMemo<PushHandlerMap>(() => ({
        admin_test_push: (notification) => {
            navigateToDeepLink(notification.deep_link);
        },
        carta_porte_stamped: (notification) => {
            void refreshContractsFromPush(notification);
            navigateToDeepLink(notification.deep_link);
        },
        chat_new_message: (notification) => {
            void refreshChatFromPush(notification);
            navigateToDeepLink(notification.deep_link);
        },
        shipment_quote_received: (notification) => {
            navigateToDeepLink(notification.deep_link);
        },
        shipment_quote_selected: (notification) => {
            navigateToDeepLink(notification.deep_link);
        },
        shipment_request_match_found: (notification) => {
            void refreshRequestsFromPush(notification);
            navigateToDeepLink(notification.deep_link);
        },
        contract_invitation_assigned: (notification) => {
            void refreshContractsFromPush(notification);
            navigateToDeepLink(notification.deep_link);
        },
        driver_shift_assigned: (notification) => {
            void refreshDriverShiftFromPush();
            navigateToDeepLink(notification.deep_link || '/driver/shift');
        },
        tracking: (notification) => {
            void refreshContractsFromPush(notification);
            handleTrackingNavigation(notification);
        },
        security_pin: (notification) => {
            void refreshContractsFromPush(notification);
            navigateToDeepLink(notification.deep_link);
        },
        provider_payout_paid: (notification) => {
            navigateToDeepLink(notification.deep_link);
        },
        provider_payout_missing_bank_account: (notification) => {
            navigateToDeepLink(notification.deep_link);
        },
        cargo_insurance_policy_issued: (notification) => {
            void refreshContractsFromPush(notification);
            navigateToDeepLink(notification.deep_link);
        },
    }), [navigateToDeepLink, refreshChatFromPush, refreshRequestsFromPush, refreshContractsFromPush, refreshDriverShiftFromPush, handleTrackingNavigation]);

    const foregroundHandlers = useMemo<PushHandlerMap>(() => ({
        admin_test_push: (notification) => {
            console.info('[Push] Notification recibida en foreground:', {
                type: notification.type,
                deep_link: notification.deep_link,
            });
        },
        carta_porte_stamped: (notification) => {
            console.info('[Push] Carta Porte timbrada recibida en foreground:', {
                type: notification.type,
                deep_link: notification.deep_link,
                meta: notification.meta,
            });

            void refreshContractsFromPush(notification);

            showToast({
                tone: 'info',
                title: 'Carta Porte timbrada',
                message: notification.body || 'El operador ya timbro la carta porte. Revisa el contrato y la factura.',
                durationMs: 8000,
                actionLabel: 'Abrir',
                onAction: () => {
                    navigateToDeepLink(notification.deep_link);
                },
            });
        },
        chat_new_message: (notification) => {
            const notificationConversationId = parseConversationIdFromNotification(notification);

            if (
                notificationConversationId !== null
                && activeConversationId !== null
                && notificationConversationId === activeConversationId
            ) {
                return;
            }

            console.info('[Push] Mensaje de chat recibido en foreground:', {
                type: notification.type,
                deep_link: notification.deep_link,
                meta: notification.meta,
            });
            void refreshChatFromPush(notification);
        },
        shipment_quote_received: (notification) => {
            console.info('[Push] Cotización recibida en foreground:', {
                type: notification.type,
                deep_link: notification.deep_link,
                meta: notification.meta,
            });
        },
        shipment_quote_selected: (notification) => {
            console.info('[Push] Cotización aceptada en foreground:', {
                type: notification.type,
                deep_link: notification.deep_link,
                meta: notification.meta,
            });
        },
        shipment_request_match_found: (notification) => {
            console.info('[Push] Matching de solicitud recibido en foreground:', {
                type: notification.type,
                deep_link: notification.deep_link,
                meta: notification.meta,
            });
            void refreshRequestsFromPush(notification);
        },
        contract_invitation_assigned: (notification) => {
            console.info('[Push] Invitacion de contrato recibida en foreground:', {
                type: notification.type,
                deep_link: notification.deep_link,
                meta: notification.meta,
            });
            void refreshContractsFromPush(notification);
        },
        driver_shift_assigned: (notification) => {
            console.info('[Push] Jornada asignada recibida en foreground:', {
                type: notification.type,
                deep_link: notification.deep_link,
                meta: notification.meta,
            });

            void refreshDriverShiftFromPush();

            showToast({
                tone: 'info',
                title: notification.title || 'Tienes una jornada asignada',
                message: notification.body || 'Abre Jornada para revisar tu contrato y comenzar cuando estés listo.',
                durationMs: 8000,
                actionLabel: 'Abrir',
                onAction: () => {
                    navigateToDeepLink(notification.deep_link || '/driver/shift');
                },
            });
        },
        tracking: (notification) => {
            const intentAction = resolveTrackingIntentAction(notification);
            const eventType = parseTrackingEventType(notification);

            console.info('[Push] Evento de tracking recibido en foreground:', {
                type: notification.type,
                event_type: eventType,
                action: parseTrackingAction(notification),
                deep_link: notification.deep_link,
                meta: notification.meta,
            });

            void refreshContractsFromPush(notification);

            if (!intentAction) {
                return;
            }

            const title = intentAction === 'mark_destination_arrived'
                ? 'Operador llego al destino'
                : 'Operador llego al origen';
            const message = intentAction === 'mark_destination_arrived'
                ? 'Toca confirmar para abrir el tracking y registrar la llegada.'
                : 'Toca confirmar para abrir el tracking y registrar la llegada.';

            showToast({
                tone: 'info',
                title,
                message,
                durationMs: 6000,
                actionLabel: 'Confirmar',
                onAction: () => {
                    handleTrackingNavigation(notification);
                },
            });
        },
        security_pin: (notification) => {
            console.info('[Push] PIN de seguridad recibido en foreground:', {
                type: notification.type,
                deep_link: notification.deep_link,
                meta: notification.meta,
            });

            void refreshContractsFromPush(notification);

            const pinType = (notification.rawData.pin_type ?? notification.meta?.pin_type) === 'delivery'
                ? 'entrega'
                : 'recolección';

            showToast({
                tone: 'info',
                title: 'PIN de seguridad disponible',
                message: `Tu PIN de ${pinType} está listo. Toca para abrirlo.`,
                durationMs: 8000,
                actionLabel: 'Abrir',
                onAction: () => {
                    navigateToDeepLink(notification.deep_link);
                },
            });
        },
        provider_payout_paid: (notification) => {
            showToast({
                tone: 'success',
                title: notification.title || 'Pago recibido',
                message: notification.body || 'Ya puedes revisar el depósito en tu historial de pagos.',
                durationMs: 8000,
                actionLabel: 'Abrir',
                onAction: () => {
                    navigateToDeepLink(notification.deep_link);
                },
            });
        },
        provider_payout_missing_bank_account: (notification) => {
            showToast({
                tone: 'info',
                title: notification.title || 'Agrega tu cuenta bancaria',
                message: notification.body || 'Completa tu cuenta bancaria para poder recibir pagos.',
                durationMs: 8000,
                actionLabel: 'Abrir',
                onAction: () => {
                    navigateToDeepLink(notification.deep_link);
                },
            });
        },
        cargo_insurance_policy_issued: (notification) => {
            void refreshContractsFromPush(notification);
            showToast({
                tone: 'success',
                title: notification.title || 'Tu póliza fue emitida',
                message: notification.body || 'Tu cobertura está activa.',
                durationMs: 8000,
                actionLabel: 'Ver Seguro',
                onAction: () => navigateToDeepLink(notification.deep_link),
            });
        },
    }), [activeConversationId, refreshChatFromPush, refreshRequestsFromPush, refreshContractsFromPush, refreshDriverShiftFromPush, handleTrackingNavigation, navigateToDeepLink, showToast]);

    const actionHandlersRef = useRef<PushHandlerMap>(actionHandlers);
    const foregroundHandlersRef = useRef<PushHandlerMap>(foregroundHandlers);

    useEffect(() => {
        actionHandlersRef.current = actionHandlers;
    }, [actionHandlers]);

    useEffect(() => {
        foregroundHandlersRef.current = foregroundHandlers;
    }, [foregroundHandlers]);

    const syncTokenWithBackend = useCallback(async (pushToken: string): Promise<void> => {
        if (!isAuthenticated || !userId) {
            return;
        }

        const platform = getPushPlatform();
        if (!platform) {
            return;
        }

        const syncKey = `${userId}:${pushToken}`;
        if (lastSyncedTokenRef.current === syncKey) {
            return;
        }

        try {
            await registerPushDevice({
                token: pushToken,
                platform,
                app_version: APP_VERSION,
            });
            lastSyncedTokenRef.current = syncKey;
        } catch (error) {
            const pushError = error as PushApiError;
            console.error('[Push] Error registrando token en backend:', {
                message: pushError?.message ?? 'Error desconocido',
                error_code: pushError?.error_code,
            });
        }
    }, [isAuthenticated, userId]);

    const syncTokenRef = useRef(syncTokenWithBackend);

    useEffect(() => {
        syncTokenRef.current = syncTokenWithBackend;
    }, [syncTokenWithBackend]);

    // Inicializar listeners una sola vez y evitar listeners duplicados.
    useEffect(() => {
        if (!isPushNativeSupported()) {
            return;
        }

        void initializePushListeners({
            onRegistration: async (token: string) => {
                await syncTokenRef.current(token);
            },
            onRegistrationError: (error: unknown) => {
                console.error('[Push] Error de registro nativo:', error);
            },
            onForegroundNotification: async (payload: PushNotificationPayload) => {
                await dispatchPushByType(
                    payload,
                    foregroundHandlersRef.current,
                    async (notification) => {
                        console.info('[Push] Tipo sin handler (foreground):', notification.type);
                    }
                );
            },
            onNotificationAction: async (payload: PushNotificationPayload) => {
                await dispatchPushByType(
                    payload,
                    actionHandlersRef.current,
                    async (notification) => {
                        navigateToDeepLink(notification.deep_link);
                    }
                );
            },
        });

        return () => {
            void teardownPushListeners();
        };
    }, [navigateToDeepLink]);

    // Pedir permisos y registrar cliente push cuando hay sesión activa.
    useEffect(() => {
        if (!isPushNativeSupported()) {
            return;
        }

        if (!isAuthenticated) {
            lastSyncedTokenRef.current = null;
            return;
        }

        let cancelled = false;

        const bootstrapPush = async () => {
            const permission = await ensurePushPermissions();
            if (permission !== 'granted' || cancelled) {
                return;
            }

            await registerPushClient();
            if (cancelled) {
                return;
            }

            const cachedToken = getCachedPushToken();
            if (cachedToken) {
                await syncTokenRef.current(cachedToken);
            }
        };

        void bootstrapPush();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, authToken]);

    return <>{children}</>;
}
